import React, { useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { createFileName, processUploadedFile } from '../../util/GenericUtil';
import { validateNewTestInputs, getNewTestWarnings } from '../../util/UploadValidation';
import { useToast } from '../Toast/ToastProvider';
import { uploadFile } from '../API';
import { cacheFiles } from '../../util/FileCacheUtil';

import TestChartEntry from '../TestChartEntry/TestChartEntry';

function NewTestForm({ setSelectedFile, normalizeTestName, newTest, setNewTest, edit, userFiles, setUserFiles, setUploadingState, patientId, userId }) {
  const [newEntrySelected, setNewEntrySelected] = useState([]);
  const [showError, setShowError] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const [selectMode, setSelectMode] = useState(false)
  const { showToast } = useToast();

  const performUpload = async () => {
    setShowWarning(false);
    setUploadingState?.(true);

    try {
      const fileData = {
        ...newTest.data,
        header: { ...newTest.data.header, type: 'bloodtest', source: 'manualEntry' },
      };

      const uploadedFile = await uploadFile(
        new File([JSON.stringify(fileData)], edit ? newTest.key : createFileName(fileData.header), { type: 'application/json' }),
        patientId
      );

      const processedFileObj = processUploadedFile(uploadedFile, normalizeTestName);

      flushSync(() => {
        setUserFiles(prevFiles => {
          let updatedFiles;
          if (edit) {
            updatedFiles = prevFiles.map(file =>
              file.key === uploadedFile.key ? processedFileObj : file
            );
          } else {
            updatedFiles = [...(prevFiles || []), processedFileObj];
          }
          const cacheId = patientId || userId;
          if (cacheId) cacheFiles(cacheId, updatedFiles);
          return updatedFiles;
        });
      });

      setNewTest(null);
      setSelectedFile('all');
    } catch (error) {
      console.error('Manual entry upload error:', error);
      showToast('Failed to save test entry. Please try again.', 'error');
    } finally {
      setUploadingState?.(false);
    }
  };

  const entryRefs = useRef(null)
  const dragJustEnded = useRef(false)

  const handleInputChange = (field, value) => {
    setShowWarning(false);
    setNewTest({
      ...newTest,
      data: { ...newTest.data, header: { ...newTest.data.header, [field]: value } },
    });
  };

  // Find the first available field that can receive focus in a specific row
  const findFirstAvailableFieldInRow = (rowIndex) => {
    const fieldOrder = ['test_name', 'result', 'units', 'lab_low', 'lab_high'];
    
    for (const fieldName of fieldOrder) {
      const field = document.querySelector(`input[data-property="${fieldName}"][data-index="${rowIndex}"]`);
      if (field && !field.disabled && !field.readOnly) {
        return field;
      }
    }
    
    return null;
  };

  const handleUpload = async () => {
    if (!validateNewTestInputs(newTest.data)) {
      showToast('Please fill in required fields (test name and result)', 'error');
      setShowError(true);
      return;
    }

    const warnings = getNewTestWarnings(newTest.data);
    if (warnings.length > 0) {
      setShowWarning(true);
      showToast('Some entries are missing units or reference ranges', 'warning', 8000, {
        label: 'Save Anyway',
        onClick: performUpload,
      });
      return;
    }

    performUpload();
  };

  useEffect(() => {
    entryRefs.current = Array.from(document.querySelectorAll(".temp"))
  }, [newTest])

  return (
    <div className="newTestForm">
      <div className="newTestHeader">
        <div className="newTestFunctions">
          <button
            className="cancel"
            onClick={() => {
              setSelectedFile('all');
              setNewTest(null);
            }}>
            Delete Draft
          </button>
          <button className="upload" onClick={handleUpload}>
            Upload
          </button>
        </div>
        <p className="headerTitle">Patient Information</p>
        <div className="patientInfo">
          {['first_name', 'last_name', 'birthdate', 'sex'].map((field) => (
            <div
              key={field}
              className={`inputContainer ${showError && !newTest.data.header[field] ? 'error' : ''}`}>
              {field !== 'sex' ? (
                <input
                  type={field === 'birthdate' ? 'date' : 'text'}
                  value={newTest.data.header[field] || ''}
                  onChange={(e) => handleInputChange(field, e.target.value)}
                />
              ) : (
                <select
                  value={newTest.data.header.sex}
                  onChange={(e) => handleInputChange('sex', e.target.value)}>
                  <option value="M">Male</option>
                  <option value="F">Female</option>
                </select>
              )}
              <p className="topLeftLabel">
                {field.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
              </p>
            </div>
          ))}
        </div>
        <p className="headerTitle">Test Information</p>
        <div className="testInfo">
          <div className={`inputContainer ${showError && !newTest.data.header.testdate ? 'error' : ''}`}>
            <input
              type="date"
              value={newTest.data.header.testdate ? newTest.data.header.testdate.toString().replace(/(\d{4})(\d{2})(\d{2})/, "$1-$2-$3") : ""}

              onChange={(e) => {
                const formattedDate = parseInt(e.target.value.replace(/-/g, ""));
                handleInputChange('testdate', formattedDate)}}
            />
            <p className="topLeftLabel">Test Date</p>
          </div>
        </div>
      </div>
      <div className="entryFunctions">
        <button onClick={() => {
          if (selectMode) {
            setNewEntrySelected([])
          }
          setSelectMode(!selectMode)
          }}>Select Mode</button>
        <button
          className="delete"
          disabled={newEntrySelected.length === 0}
          onClick={() => {
            setNewTest({
              ...newTest,
              data: {
                ...newTest.data,
                body: newTest.data.body.filter((_, i) => !newEntrySelected.includes(i)),
              },
            });
            setNewEntrySelected([]);
          }}>
          Delete Selected ({newEntrySelected.length})
        </button>
      </div>
      {newTest.data.body.map((test, i) => (
        <TestChartEntry
          showError={showError}
          showWarning={showWarning}
          key={i}
          temp={true}
          test={test}
          index={i}
          setNewTest={(...args) => { setShowWarning(false); setNewTest(...args); }}
          newTest={newTest}
          setNewEntrySelected={setNewEntrySelected}
          newEntrySelected={newEntrySelected}
          entryRefs={entryRefs}
          selectMode={selectMode}
          dragJustEnded={dragJustEnded}
        />
      ))}
      <button
        className="addRow"
        onClick={() => {
          const newRowIndex = newTest.data.body.length;
          
          setNewTest({
            ...newTest,
            data: { ...newTest.data, body: [...newTest.data.body, {}] },
          });

          // Move cursor to test name field of newly created row
          setTimeout(() => {
            try {
              // Find the test name input in the newly created row
              const testNameInput = document.querySelector(`input[data-property="test_name"][data-index="${newRowIndex}"]`);
              
              // Check if test name field exists and is not disabled
              if (testNameInput && !testNameInput.disabled && !testNameInput.readOnly) {
                testNameInput.focus();
              } else {
                // If test name field doesn't exist or is disabled, try to focus the first available field in the new row
                const firstAvailableField = findFirstAvailableFieldInRow(newRowIndex);
                if (firstAvailableField) {
                  firstAvailableField.focus();
                }
              }
            } catch (error) {
              // Silently handle any focus errors - don't break the user experience
              console.warn('Could not move cursor to test name field in new row:', error);
            }
          }, 100); // Timing to ensure DOM is ready before focusing
        }}
        onKeyDown={(e) => {
          // Allow Enter key to activate the button when focused
          if (e.key === 'Enter') {
            e.preventDefault();
            e.target.click();
          }
        }}>
        Add Row
      </button>
    </div>
  );
}

export default NewTestForm;